/**
 * hooks pra documento de pesquisa.
 *
 * download usa fetch raw (apiFetch espera JSON; PDF e bytes binarios).
 * cookie de sessao vai via credentials: 'include'.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DocumentoPesquisa } from '@licita-mind/shared-types';
import { ApiError, apiFetch } from '../lib/api';

export const documentoKey = (pesquisaId: string) => ['documento', pesquisaId] as const;

export const useGetDocumento = (pesquisaId: string | undefined) =>
  useQuery({
    queryKey: documentoKey(pesquisaId ?? '__none__'),
    queryFn: async () => {
      try {
        return await apiFetch<DocumentoPesquisa>(`/v1/pesquisas/${pesquisaId}/documento`);
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) return null;
        throw e;
      }
    },
    enabled: !!pesquisaId,
    staleTime: 5_000,
  });

export const useGerarDocumento = (pesquisaId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<DocumentoPesquisa>(`/v1/pesquisas/${pesquisaId}/documento`, { method: 'POST' }),
    onSuccess: async (doc) => {
      qc.setQueryData(documentoKey(pesquisaId), doc);
    },
  });
};

/**
 * dispara download do PDF via fetch raw + blob -> URL temporaria -> click sintetico.
 * mais robusto que `window.location.href = url` porque preserva header cookie
 * e nao polui historico de navegacao.
 */
export const baixarDocumento = async (pesquisaId: string): Promise<void> => {
  const res = await fetch(`/api/v1/pesquisas/${pesquisaId}/documento/download`, {
    credentials: 'include',
  });
  if (!res.ok) {
    throw new Error(`falha no download: ${res.status} ${res.statusText}`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `pesquisa-${pesquisaId.slice(0, 8)}.pdf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // libera memoria apos um tick (browser ja iniciou download)
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};
